// src/utils/followUpPlan.js
// Pure schedule logic for the Past Client Follow-Ups program. No React, no
// Firestore — everything here is unit-testable with plain data.
// Program (approved 2026-08-11): 1, 2, 3, 6, 9 months after closing, then
// the 1-year home anniversary and every anniversary after, forever.
import moment from "moment";
import { dealContactIds } from "./contactCrm";
import { isOpen, notDeleted, ownedBy } from "./statusSheetData";
import { creditedAgentId } from "./dealCredit";
import { normalizeContact } from "./contactNormalize";

const MONTH_OFFSETS = [1, 2, 3, 6, 9];

const iso = (m) => m.format("YYYY-MM-DD");
const todayIso = () => iso(moment());

// The program covers closings from this date forward ("new closings only",
// Henry, 2026-08-11). The backstop/gate below must never pull older deals in.
export const PROGRAM_START = "2026-08-11";

// Strict ISO (YYYY-MM-DD) check before the string comparison against
// PROGRAM_START — display-format dates like "5/1/2018" sort lexically above
// "2026-08-11" and would otherwise let an old closing enroll with a fresh
// schedule.
export const isoOnOrAfterProgramStart = (d) => {
  const closing = String(d?.closingdate || "").trim();
  return /^\d{4}-\d{2}-\d{2}$/.test(closing) && closing >= PROGRAM_START;
};

// moment's add(months) already clamps Jan 31 + 1 month → Feb 28.
export function buildTouches(closingDate) {
  const base = moment(closingDate, "YYYY-MM-DD", true);
  const touches = {};
  MONTH_OFFSETS.forEach((n) => {
    touches[`month-${n}`] = {
      due: iso(base.clone().add(n, "months")),
      state: "pending",
    };
  });
  touches["anniversary-1"] = {
    due: iso(base.clone().add(1, "years")),
    state: "pending",
  };
  return touches;
}

// closingdate arrives as "YYYY-MM-DD" on well-formed deals, but legacy records
// carry display strings, numbers, or junk. Strict-parse; anything else falls
// back to today so a plan is never built on "Invalid date".
function resolveClosingDate(deal, today) {
  const raw = String(deal?.closingdate ?? "").trim();
  return moment(raw, "YYYY-MM-DD", true).isValid() ? raw : today;
}

// Two clients sharing a last name compress to "Sarah and Mike Johnson";
// otherwise a plain " and " join. Shared by householdNames (below) and by
// the page's clientNames self-heal path, which only has fetched contacts on
// hand (no deal) — takes anything with a `.name`, already-normalized or not.
export function householdNamesFromContacts(contacts) {
  const names = (contacts || [])
    .map((c) => String(c?.name || "").trim())
    .filter(Boolean);
  if (names.length === 2) {
    const [a, b] = names.map((n) => n.split(" "));
    const lastA = a.slice(1).join(" ");
    const lastB = b.slice(1).join(" ");
    if (lastA && lastA === lastB) return `${a[0]} and ${b[0]} ${lastB}`;
  }
  return names.join(" and ");
}

// Full names via normalizeContact (handles both firstName/lastName and
// legacy single-name contact docs); a blank resolved name falls through to
// the clientName carried on the deal's customers[] entry.
function householdNames(deal, contacts) {
  const byId = new Map((contacts || []).map((c) => [c.id, c]));
  const resolved = dealContactIds(deal).map((id) => {
    const c = byId.get(id);
    const name = c ? (normalizeContact(c).name || "").trim() : "";
    if (name) return { name };
    const raw = (deal?.customers || []).find(
      (x) => x && typeof x === "object" && x.contactId === id,
    );
    return { name: (raw?.clientName || "").trim() };
  });
  return householdNamesFromContacts(resolved);
}

// `contacts` are the resolved contact docs for this deal's customers (the
// caller fetches them; this stays pure). createdAt is stamped by the store.
export function buildFollowUpPlan(deal, contacts = [], today = todayIso()) {
  const closingDate = resolveClosingDate(deal, today);
  return {
    dealId: String(deal?.id || deal?.createdat || ""),
    dealCreatedat: String(deal?.createdat || ""),
    // Whoever is credited with the sale is the one who keeps in touch with
    // that past client. Same rule missedClosings uses to decide whose closing
    // needs a plan, so the plan can never be stamped with the other person.
    agentId: creditedAgentId(deal),
    side: String(deal?.dealtype || "").toLowerCase() === "seller" ? "seller" : "buyer",
    clientIds: dealContactIds(deal),
    clientNames: householdNames(deal, contacts),
    propertyAddress: deal?.propertyaddress || "",
    closingDate,
    ...(closingDate !== String(deal?.closingdate ?? "").trim() && { needsReview: true }),
    status: "active",
    touches: buildTouches(closingDate),
  };
}

export function touchLabel(key) {
  const m = /^month-(\d+)$/.exec(key);
  if (m) return `${m[1]}-month check-in`;
  const a = /^anniversary-(\d+)$/.exec(key);
  if (a) return `${a[1]}-year home anniversary`;
  return key;
}

// bucket: "due" (today or past, still pending), "upcoming", "resolved".
// overdueDays counts from the ORIGINAL due date even after a snooze: snoozing
// hides the row for a while but does not erase how late the touch is.
export function dueState(touch, today = todayIso()) {
  if (!touch || touch.state !== "pending") return { bucket: "resolved", overdueDays: 0 };
  const effectiveDue = touch.snoozedUntil && touch.snoozedUntil > touch.due
    ? touch.snoozedUntil
    : touch.due;
  if (effectiveDue > today) return { bucket: "upcoming", overdueDays: 0 };
  const overdueDays = moment(today, "YYYY-MM-DD").diff(moment(touch.due, "YYYY-MM-DD"), "days");
  return { bucket: "due", overdueDays: Math.max(0, overdueDays) };
}

// Exactly one future anniversary exists at a time. Roll forward when the
// latest one is resolved, OR when it has sat pending for more than 11 months
// (so an ignored anniversary can't stall the program forever) — in that case
// `staleKey` names the pending touch the caller should mark skipped.
export function nextAnniversaryEntry(plan, today = todayIso()) {
  const touches = plan?.touches || {};
  const keys = Object.keys(touches)
    .filter((k) => k.startsWith("anniversary-"))
    .sort((x, y) => Number(x.split("-")[1]) - Number(y.split("-")[1]));
  const last = keys[keys.length - 1];
  if (!last) return null;
  const lastTouch = touches[last];
  const lastN = Number(last.split("-")[1]);
  const base = moment(plan.closingDate, "YYYY-MM-DD");
  // First anniversary strictly after today, so catch-up is one write even
  // after years of dormancy.
  let n = lastN + 1;
  while (iso(base.clone().add(n, "years")) < today) n += 1;
  const next = {
    key: `anniversary-${n}`,
    touch: { due: iso(base.clone().add(n, "years")), state: "pending" },
  };
  if (lastTouch.state !== "pending") return next;
  const staleBefore = iso(moment(today, "YYYY-MM-DD").subtract(11, "months"));
  if (lastTouch.due < staleBefore) return { ...next, staleKey: last };
  return null;
}

// When a retracted plan is revived, completed history must survive: start
// from the fresh schedule, then carry over every previously RESOLVED touch
// (done/skipped) wholesale — an agent's recorded work is never re-asked,
// even if the re-closing moved the dates. Pending old touches are dropped in
// favor of the fresh schedule.
export function mergeRevivedTouches(prevTouches, nextTouches) {
  const merged = { ...nextTouches };
  Object.entries(prevTouches || {}).forEach(([key, touch]) => {
    if (touch && touch.state !== "pending") merged[key] = touch;
  });
  return merged;
}

// A household mid-transaction shouldn't get "how's the house?" texts. Only a
// genuinely open deal pauses (cancelled/expired/deleted deals don't).
// Computed at read time from deals already in app state, not stored.
export function planIsPausedBy(plan, deals) {
  const mine = new Set(plan?.clientIds || []);
  if (!mine.size) return false;
  return (deals || []).some(
    (d) =>
      isOpen(d) &&
      notDeleted(d) &&
      dealContactIds(d).some((id) => mine.has(id)),
  );
}

// Deals needing a backstop plan: closed, not deleted, owned by this user, on
// or after PROGRAM_START, and not already represented by a plan. `plans`
// (not just active ones) build the have-set so an ended plan doesn't get
// re-created every login — except a "deal-reopened" (retracted) plan, which
// must stay eligible for revival by ensureFollowUpPlan.
//
// The have-set is keyed on dealId and NOTHING ELSE, deliberately. Plan docs
// written before the credit rule was settled carry the agent's id in agentId
// where the rule now names the team lead (an in-house referral), so "is this
// deal already covered?" must never be asked as "is it covered BY ME?" — that
// would look at a real, live plan, decide the deal was unenrolled, and try to
// enroll it a second time. Skipping on the dealId alone can only ever prevent
// a duplicate; it can never remove, overwrite, or re-date an existing plan,
// which is the safe side to err on for a program that is already running with
// real client touches recorded against it.
export function missedClosings(deals, plans, userId) {
  const have = new Set(
    (plans || [])
      .filter((p) => !String(p.endedReason || "").startsWith("deal-reopened"))
      .map((p) => p.dealId),
  );
  return (deals || []).filter(
    (d) =>
      d?.dealStage === "Closed" &&
      notDeleted(d) &&
      ownedBy(d, userId) &&
      isoOnOrAfterProgramStart(d) &&
      !have.has(String(d?.id || d?.createdat || "")),
  );
}

// Plan docs whose stored agentId no longer matches who their deal credits,
// paired with the id they should carry. Read-only and idempotent: run it twice
// and the second run returns an empty list, because the first run's writes make
// every plan agree with its deal.
//
// This exists because agentId is FROZEN into the plan doc the day it is
// written, while credit is recomputed from the deal every time it is asked.
// A plan written before the in-house-referral rule was settled sits in the
// referring agent's queue while every report says the sale belongs to the team
// lead. Nothing is lost either way (the touches, the dates and the client names
// are all untouched by this), so this is a tidy-up, not a repair.
//
// Guard rails, in order of how much damage each one prevents:
//   - a plan whose deal cannot be found is skipped, so a deleted or archived
//     deal can never blank out a live plan's owner;
//   - a deal that credits nobody is skipped, so a real id is never replaced
//     with an empty string;
//   - a plan that already agrees with its deal is skipped, which is what makes
//     re-running this free.
//
// The caller applies these with reassignFollowUpPlan(planId, agentId) — one
// field, one write, no doc is created or destroyed.
export function planAgentBackfills(plans, deals) {
  const byId = new Map(
    (deals || []).map((d) => [String(d?.id || d?.createdat || ""), d]),
  );
  const out = [];
  (plans || []).forEach((plan) => {
    const deal = byId.get(String(plan?.dealId || ""));
    if (!deal) return;
    const shouldBe = creditedAgentId(deal);
    if (!shouldBe) return;
    const stored = String(plan?.agentId || "");
    if (stored === shouldBe) return;
    out.push({ planId: plan.dealId, agentId: shouldBe, from: stored });
  });
  return out;
}

// Active plans whose own deal has moved off Closed. The stage-pill hook and
// the board's handleDragEnd (drag AND tap-menu, which routes through it)
// both retract directly; this catches what those two miss — an edit-form
// save, a bulk upload, an integration — on the owner's next login. A plan
// whose deal can't be found (e.g. deleted) is left alone.
export function retractablePlans(plans, deals) {
  return (plans || [])
    .filter((p) => p.status === "active")
    .filter((p) => {
      const deal = (deals || []).find(
        (d) => String(d?.id || d?.createdat || "") === p.dealId,
      );
      return Boolean(deal) && deal.dealStage !== "Closed";
    });
}

// Newly-due touches for THIS user that haven't fired a push yet. Paused
// plans never notify — same rule the queue page applies.
export function selectNotifiable(plans, userId, deals = [], today = todayIso()) {
  const out = [];
  (plans || []).forEach((plan) => {
    if (plan.status !== "active" || plan.agentId !== userId) return;
    if (planIsPausedBy(plan, deals)) return;
    Object.entries(plan.touches || {}).forEach(([touchKey, touch]) => {
      if (touch.notifiedAt) return;
      if (dueState(touch, today).bucket !== "due") return;
      out.push({ planId: plan.dealId, touchKey, clientNames: plan.clientNames });
    });
  });
  return out;
}
